import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Para obtener comId de la URL y navegar
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Fab,
    TablePagination,
    TextField,
    InputAdornment,
    Chip,
    Card, CardContent,
    Divider,
    Grid
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon,
    Engineering as EngineeringIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon
} from "@mui/icons-material";
import { format, parseISO } from 'date-fns';

import MaintenanceForm from "../utils/MaintenanceForm";
import { useMaintenances } from "../../../hooks/maintenance/useMaintenances";
import { useAuthContext } from "../../../context/AuthProvider";
import { useComponents } from "../../../hooks/maintenance/useComponent";
import { useComponentDetails } from "../../../hooks/maintenance/useComponentDetails";

const MaintenanceByComponentDashboard = () => {
    const { comId } = useParams();
    const parsedComId = parseInt(comId, 10);
    const isValidComId = !isNaN(parsedComId) && parsedComId > 0;
    const navigate = useNavigate();

    const [formState, setFormState] = useState({ open: false, maintenance: null });
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, maintenance: null });
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [inMaintenance, setInMaintenance] = useState(false);

    const { user } = useAuthContext();

    const {
        maintenances,
        isLoading: isLoadingMaintenances,
        error: errorMaintenances,
        createMutation,
        updateMutation,
        deleteMutation
    } = useMaintenances(isValidComId ? parsedComId : undefined);

    const {
        data: componentDetails,
        isLoading: isLoadingComponent,
        error: errorComponent
    } = useComponentDetails(isValidComId ? parsedComId : undefined);

    // --- NUEVA LÓGICA para separar el mantenimiento en curso del historial ---
    const [currentMaintenance, setCurrentMaintenance] = useState(null);
    const [historyMaintenances, setHistoryMaintenances] = useState([]);

    // --- Efecto para calcular `inMaintenance` cuando `maintenances` cambia ---
    useEffect(() => {
        if (maintenances && maintenances.length > 0) {
            const ongoing = maintenances.find(maint => maint.manest !== 'FINALIZADO');
            // El historial son todos los que SÍ están FINALIZADOS
            const history = maintenances.filter(maint => maint.manest === 'FINALIZADO');

            setCurrentMaintenance(ongoing || null);
            setHistoryMaintenances(history);
            setInMaintenance(!!ongoing);
        }
    }, [maintenances]);

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleOpenForm = (maintenance = null) => {
        if (!maintenance && inMaintenance) {
            showAlert("No se puede crear un nuevo mantenimiento si ya existe uno en progreso para este componente.", "warning");
            return;
        }
        setFormState({ open: true, maintenance });
    };

    const handleCloseForm = () => {
        setFormState({ open: false, maintenance: null });
    };

    // --- Manejo del Submit del Formulario ---
    const handleSubmit = useCallback((data) => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión para realizar esta acción.", "error");
            return;
        }

        const payload = { ...data };
        payload.comid = parseInt(comId, 10); // Aseguramos que el comid del componente actual se adjunte

        // Convertir fechas de objeto Date a ISO string para el backend
        if (payload.manfecini) {
            payload.manfecini = format(payload.manfecini, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"); // ISO con zona horaria
        }
        if (payload.manfecfin) {
            payload.manfecfin = format(payload.manfecfin, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"); // ISO con zona horaria
        } else {
            payload.manfecfin = null; // Si no hay fecha de fin, enviar null
        }


        if (formState.maintenance) { // Edición
            payload.updbyid = user.EmpID;
            updateMutation.mutate(
                { id: formState.maintenance.manid, data: payload },
                {
                    onSuccess: () => {
                        showAlert("Mantenimiento actualizado correctamente");
                        handleCloseForm();
                    },
                    onError: (mutationError) => {
                        console.error("Error al actualizar mantenimiento:", mutationError);
                        showAlert(mutationError.message || "Error al actualizar", "error");
                    },
                }
            );
        } else { // Creación
            payload.crebyid = user.EmpID;
            createMutation.mutate(payload, {
                onSuccess: () => {
                    showAlert("Mantenimiento creado correctamente");
                    handleCloseForm();
                },
                onError: (mutationError) => {
                    console.error("Error al crear mantenimiento:", mutationError);
                    showAlert(mutationError.message || "Error al crear", "error");
                },
            });
        }
    }, [formState.maintenance, createMutation, updateMutation, user, comId]);

    // --- Manejo de Confirmación de Eliminación ---
    const handleConfirmDelete = () => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión para realizar esta acción.", "error");
            setConfirmDialog({ open: false, maintenance: null });
            return;
        }
        deleteMutation.mutate(
            { id: confirmDialog.maintenance.manid, updbyid: user.EmpID }, // Pasar ID y updbyid
            {
                onSuccess: () => {
                    showAlert("Mantenimiento eliminado lógicamente correctamente");
                    setConfirmDialog({ open: false, maintenance: null });
                },
                onError: (mutationError) => {
                    console.error("Error al eliminar mantenimiento:", mutationError);
                    showAlert(mutationError.message || "Error al eliminar", "error");
                },
            }
        );
    };

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    // Filtrar mantenimientos por término de búsqueda (nombre de tipo de mantenimiento, observaciones)
    /*const filteredMaintenances = maintenances.filter((item) =>
        item.tipomantenimientonombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.manobs && item.manobs.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.manest.toLowerCase().includes(searchTerm.toLowerCase())
    );*/

    const filteredHistoryMaintenances = historyMaintenances.filter((item) =>
        item.tipomantenimientonombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.manobs && item.manobs.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.manest.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getComponentStatusChipColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVO': return 'success';
            case 'MANTENIMIENTO': return 'warning';
            default: return 'default';
        }
    };

    const getMaintenanceStatusChipColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'CREADO': return 'info';
            case 'INICIADO': return 'primary';
            case 'EN_PROCESO': return 'warning';
            case 'FINALIZADO': return 'success';
            case 'CANCELADO': return 'error';
            default: return 'default';
        }
    }

    const getMaintenanceStatusChipIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'INICIADO':
            case 'EN_PROCESO': return <PlayArrowIcon />;
            case 'FINALIZADO': return <CheckCircleIcon />;
            case 'CANCELADO': return <CancelIcon />;
            default: return null;
        }
    }

    // Determinar el color del Chip de estado
    const getChipColor = (status) => {
        switch (status.toUpperCase()) {
            case 'CREADO': return 'info';
            case 'INICIADO': return 'primary';
            case 'EN_PROCESO': return 'warning';
            case 'FINALIZADO': return 'success';
            case 'CANCELADO': return 'error';
            default: return 'default';
        }
    };

    // Función para formatear fechas y horas (asumiendo TIMESTAMP WITH TIME ZONE)
    const formatDateTime = (dateString) => {
        if (!dateString) return '—';
        try {
            // parseISO puede manejar cadenas ISO 8601 (incluyendo las de TZ)
            return format(parseISO(dateString), 'dd/MM/yyyy HH:mm');
        } catch (e) {
            console.warn("Could not parse date:", dateString, e);
            return dateString;
        }
    };

    // Manejo de estados de carga y error del COMPONENTE
    if (isLoadingComponent) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Cargando detalles del componente...</Typography>
            </Paper>
        );
    }

    if (errorComponent && !componentDetails) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="error" variant="h6">Error al cargar detalles del componente: {errorComponent.message}</Typography>
                <Button onClick={() => navigate(`/operaciones/vehiculo/${componentDetails?.vehid}/componentes`)} startIcon={<ArrowBackIcon />}>
                    Volver a Componentes
                </Button>
            </Paper>
        );
    }

    if (!componentDetails) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="text.secondary" variant="h6">Componente no encontrado.</Typography>
                <Button onClick={() => navigate(`/operaciones/vehiculo/${componentDetails?.vehid}/componentes`)} startIcon={<ArrowBackIcon />}>Volver a Lista de Componentes</Button>
            </Paper>
        );
    }

    // --- Manejo de estados de carga y error de la LISTA DE MANTENIMIENTOS ---
    if (isLoadingMaintenances) { // Usamos el isLoading de la lista de mantenimientos
        return (
            <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Cargando mantenimientos...</Typography>
            </Paper>
        );
    }

    if (errorMaintenances) { // Usamos el error de la lista de mantenimientos
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="error" variant="h6">Error al cargar mantenimientos 222: {errorMaintenances.message}</Typography>
                <Button onClick={() => navigate(`/operaciones/vehiculo/${componentDetails?.vehid}/componentes`)} startIcon={<ArrowBackIcon />}>
                    Volver a Componentes
                </Button>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={() => navigate(`/operaciones/vehiculo/${componentDetails?.vehid}/componentes`)} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5">Mantenimientos del Componente</Typography>
                </Box>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar mantenimiento..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Información del Componente Actual y su Vehículo */}
            {/*<Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Componente: <Typography component="span" variant="h6" color="text.primary">
                            {componentDetails.comnom || 'N/A'} (ID: {componentDetails.comid})
                        </Typography>
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Tipo: {componentDetails.tipocomponentenombre || 'N/A'}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Vehículo: {componentDetails.vehiculomatricula || 'N/A'} - {componentDetails.vehiculomodelo || 'N/A'}
                    </Typography>
                </CardContent>
            </Card>*/}

            {/* Información del Vehículo Actual */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Vehículo: <Typography component="span" variant="h6" color="text.primary">
                            {componentDetails.vehiculomatricula} - {componentDetails.vehiculomodelo} ({componentDetails.vehani})
                        </Typography>
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Componente: <Typography component="span" variant="subtitle1" color="text.primary">
                            {componentDetails.comnom || 'N/A'} (Tipo: {componentDetails.tipocomponentenombre || 'N/A'})
                        </Typography>
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Estado del Componente: <Chip label={componentDetails.comest} color={getComponentStatusChipColor(componentDetails.comest)} size="small" sx={{ ml: 1 }} />
                    </Typography>
                </CardContent>
            </Card>

            {/* Sección de Mantenimiento Actual (si existe) */}
            {currentMaintenance ? (
                <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.main', borderWidth: 2 }}> {/* Borde más notorio */}
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6" color="primary.main">Mantenimiento Actual en Curso</Typography>
                            <Box>
                                <Tooltip title="Editar Mantenimiento Actual">
                                    <IconButton size="small" onClick={() => handleOpenForm(currentMaintenance)}>
                                        <EditIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Ver Detalles del Mantenimiento">
                                    <IconButton size="small" onClick={() => navigate(`/operaciones/componente/mantenimiento/${currentMaintenance.manid}`)}>
                                        <EngineeringIcon color="action" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar Mantenimiento (Lógica)">
                                    <IconButton size="small" onClick={() => setConfirmDialog({ open: true, maintenance: currentMaintenance })}>
                                        <DeleteIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Tipo:</strong> {currentMaintenance.tipomantenimientonombre || 'N/A'}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Estado:</strong> <Chip label={currentMaintenance.manest} color={getMaintenanceStatusChipColor(currentMaintenance.manest)} size="small" icon={getMaintenanceStatusChipIcon(currentMaintenance.manest)} sx={{ ml: 1 }} />
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Fecha Inicio:</strong> {formatDateTime(currentMaintenance.manfecini)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Kilometraje:</strong> {currentMaintenance.mankm ? `${currentMaintenance.mankm} km` : 'N/A'}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Costo Estimado:</strong> {currentMaintenance.costototal ? `$${parseFloat(currentMaintenance.costototal).toFixed(2)}` : 'N/A'}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Observaciones:</strong> {currentMaintenance.manobs || 'Sin observaciones.'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                <Alert severity="info" sx={{ mb: 3 }}>
                    No hay ningún mantenimiento en curso para este componente. Puedes crear uno nuevo.
                </Alert>
            )}

            {/* Sección del Historial de Mantenimientos */}
            <Typography variant="h6" mb={2}>Historial de Mantenimientos Finalizados</Typography>



            <TableContainer component={Paper} elevation={1}>
                <Table>
                    <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
                        <TableRow>
                            {/*<TableCell><strong>ID</strong></TableCell>*/}
                            <TableCell><strong>Fecha Inicio</strong></TableCell>
                            <TableCell><strong>Fecha Fin</strong></TableCell>
                            <TableCell><strong>Tipo Mantenimiento</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell><strong>KM</strong></TableCell>
                            <TableCell><strong>Costo Total</strong></TableCell>
                            <TableCell><strong>Obs.</strong></TableCell>
                            <TableCell align="center"><strong>Acciones</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredHistoryMaintenances.length > 0 ? (
                            filteredHistoryMaintenances.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((maintenance) => (
                                <TableRow key={maintenance.manid}>
                                    {/*<TableCell>{maintenance.manid}</TableCell>*/}
                                    <TableCell>{formatDateTime(maintenance.manfecini)}</TableCell>
                                    <TableCell>{formatDateTime(maintenance.manfecfin)}</TableCell>
                                    <TableCell>{maintenance.tipomantenimientonombre}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={maintenance.manest}
                                            color={getChipColor(maintenance.manest)}
                                            size="small"
                                        />
                                    </TableCell>

                                    <TableCell>{maintenance.mankm || '—'}</TableCell>
                                    <TableCell>{maintenance.costototal ? `$${parseFloat(maintenance.costototal).toFixed(2)}` : '—'}</TableCell>
                                    <TableCell>{maintenance.manobs || '—'}</TableCell>
                                    <TableCell align="center">
                                        {(maintenance.manest !== 'FINALIZADO') && (
                                            <Tooltip title="Editar Mantenimiento">
                                                <IconButton size="small" onClick={() => handleOpenForm(maintenance)}>
                                                    <EditIcon color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {(maintenance.manest !== 'FINALIZADO') && (
                                            <Tooltip title="Eliminar Mantenimiento (Lógica)">
                                                <IconButton size="small" onClick={() => setConfirmDialog({ open: true, maintenance })}>
                                                    <DeleteIcon color="error" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {/* Si hay una vista de detalle para mantenimiento */}
                                        <Tooltip title="Ver Detalles">
                                            <IconButton size="small" onClick={() => navigate(`/operaciones/componente/mantenimiento/${maintenance.manid}`)}>
                                                <VisibilityIcon color="action" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    No hay mantenimientos para este componente o no se encontraron resultados.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredHistoryMaintenances.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* FAB para crear nuevo mantenimiento */}
            <Fab
                color="primary"
                sx={{ position: "fixed", bottom: 20, right: 20 }}
                onClick={() => handleOpenForm()}
                disabled={inMaintenance}
            >
                <AddIcon />
            </Fab>

            {/* Modal para Crear/Editar Mantenimiento */}
            {formState.open && (
                <MaintenanceForm
                    open={formState.open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmit}
                    defaultValues={{
                        ...formState.maintenance,
                        comid: parseInt(comId, 10), // Aseguramos que el ComID esté en los valores por defecto del formulario
                        componentenombre: componentDetails?.comnom, // Pasar nombre del componente para mostrar en el form
                        vehiculomatricula: componentDetails?.vehiculomatricula, // Pasar matrícula para contexto
                    }}
                />
            )}

            {/* Diálogo de Confirmación de Eliminación */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, maintenance: null })}>
                <DialogTitle>Confirmar eliminación de mantenimiento</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar lógicamente el mantenimiento de tipo "{confirmDialog.maintenance?.tipomantenimientonombre}"?
                        Esto lo marcará como inactivo en el sistema y afectará el historial.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, maintenance: null })} color="inherit">Cancelar</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar de Alertas */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })}>{alert.message}</Alert>
            </Snackbar>
        </Paper>
    );
};

export default MaintenanceByComponentDashboard;